import { InputLabel } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Gender, Language } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { callLanguageCodeSelector } from 'features/call/call-base/store/selectors';
import { usePopoverContext } from 'features/menu/context';
import { handleError } from 'features/notification/store';
import type { ReturnToQueueOptions } from 'features/return-to-queue/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { SelectGender, SelectLanguage } from 'features/ui/components';
import { Button } from 'features/ui/elements';

export interface ReturnToQueueProps {
  onSubmit: (options: ReturnToQueueOptions) => void;
}

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 266px;
  padding: ${theme.spacing(9)};
  border-radius: 8px;
  background: ${theme.palette.grey[900]};
`;

const Label = styled(InputLabel)`
  margin-bottom: ${theme.spacing(3)};
`;

const LanguageLabel = styled(Label)`
  margin-top: ${theme.spacing(8)};
`;

const SubmitButton = styled(Button)`
  margin-top: ${theme.spacing(8)};
`;

const CancelButton = styled(Button)`
  margin-top: ${theme.spacing(4)};
`;

export const ReturnToQueue: FC<ReturnToQueueProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const callLanguageCode = useAppSelector(callLanguageCodeSelector);
  const { handleClosePopover } = usePopoverContext();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ReturnToQueueOptions>({
    mode: 'onChange',
    defaultValues: {
      gender: Gender.U,
      language: Language[callLanguageCode as Language],
    },
  });

  const handleReturnToQueue = async (options: ReturnToQueueOptions) => {
    setIsLoading(true);
    try {
      onSubmit(options);
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleReturnToQueue',
        })
      );
    } finally {
      setIsLoading(false);
      handleClosePopover();
    }
  };
  return (
    <Form
      onSubmit={handleSubmit(handleReturnToQueue)}
      data-testid='return-to-queue'
    >
      <Label>{t('genderPreferenceQuestion')}</Label>
      <Controller
        name='gender'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectGender value={value} onChange={onChange} />
        )}
      />
      <LanguageLabel>{t('languagePreferenceQuestion')}</LanguageLabel>
      <Controller
        name='language'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => (
          <SelectLanguage value={value} onChange={onChange} />
        )}
      />
      {isValid && (
        <>
          <SubmitButton
            type='submit'
            color='primary'
            size='small'
            fullWidth
            title={t('handoff')}
            autoFocus
            loading={isLoading}
          >
            {t('handoff')}
          </SubmitButton>
          <CancelButton
            color='secondary'
            size='small'
            fullWidth
            title={t('cancel')}
            onClick={handleClosePopover}
          >
            {t('cancel')}
          </CancelButton>
        </>
      )}
    </Form>
  );
};
